import { Schema } from "effect";
import { MessageId, SupportTicketId } from "../../../ids/definitions";
import {
  SupportTicketAssigneeType,
  SupportTicketClosureReason,
  SupportTicketStatus,
} from "./support-ticket";

export const MAX_LOCATE_SUPPORT_EMAIL_ORIGINAL_LENGTH = 200_000;

export const LocateSupportEmailInput = Schema.Struct({
  /** The full original message (headers included) pasted from the mail client. */
  originalMessage: Schema.String.pipe(
    Schema.minLength(1),
    Schema.maxLength(MAX_LOCATE_SUPPORT_EMAIL_ORIGINAL_LENGTH),
  ),
});
export type LocateSupportEmailInput = typeof LocateSupportEmailInput.Type;

export const SupportEmailProvider = Schema.Literal(
  "gmail",
  "outlook",
  "customEmail",
);
export type SupportEmailProvider = typeof SupportEmailProvider.Type;

export const LocatedSupportTicket = Schema.Struct({
  id: SupportTicketId,
  ticketNumber: Schema.Number,
  status: SupportTicketStatus,
  closureReason: Schema.NullOr(SupportTicketClosureReason),
  assigneeType: Schema.NullOr(SupportTicketAssigneeType),
});
export type LocatedSupportTicket = typeof LocatedSupportTicket.Type;

export const StoredSupportEmail = Schema.Struct({
  provider: SupportEmailProvider,
  messageId: MessageId,
  processingStatus: Schema.String,
  subject: Schema.NullOr(Schema.String),
  sentAt: Schema.DateTimeUtc,
  /** When Redo attached the message to its ticket. */
  linkedAt: Schema.NullOr(Schema.DateTimeUtc),
  /**
   * Null when Redo saved the message but no ticket holds it, or the ticket
   * has been deleted.
   */
  ticket: Schema.NullOr(LocatedSupportTicket),
});
export type StoredSupportEmail = typeof StoredSupportEmail.Type;

export const ConnectedSupportMailbox = Schema.Struct({
  provider: SupportEmailProvider,
  email: Schema.String,
  permissionsRevoked: Schema.Boolean,
});
export type ConnectedSupportMailbox = typeof ConnectedSupportMailbox.Type;

const GmailMessageCheckOutcome = Schema.Literal(
  "found",
  "notFound",
  "signInFailed",
  "unavailable",
);

export const GmailMessageCheck = Schema.Struct({
  email: Schema.String,
  outcome: GmailMessageCheckOutcome,
  /** When Gmail received the message; set only when `outcome` is `found`. */
  receivedAt: Schema.NullOr(Schema.DateTimeUtc),
  lastInboundPollAt: Schema.NullOr(Schema.DateTimeUtc),
  syncBlockedUntil: Schema.NullOr(Schema.DateTimeUtc),
});
export type GmailMessageCheck = typeof GmailMessageCheck.Type;

export const LocateSupportEmailResponse = Schema.Struct({
  messageId: Schema.NullOr(Schema.String),
  from: Schema.NullOr(Schema.String),
  /** The bare sender address from the From header, without the display name. */
  fromAddress: Schema.NullOr(Schema.String),
  /** Addresses from To, Cc, and Delivered-To, lowercased and de-duplicated. */
  recipients: Schema.Array(Schema.String),
  date: Schema.NullOr(Schema.String),
  stored: Schema.Array(StoredSupportEmail),
  mailboxes: Schema.Array(ConnectedSupportMailbox),
  /** Empty when Redo already holds the message. */
  gmailChecks: Schema.Array(GmailMessageCheck),
});
export type LocateSupportEmailResponse = typeof LocateSupportEmailResponse.Type;
